if (!customElements.get('collection-infinite-scroll')) {
  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('ul#product-grid');
      this.status = this.querySelector('[data-scroll-status]');
      this.fallback = this.querySelector('[data-scroll-fallback]');
      this.nextUrl = this.dataset.nextUrl;
      this.loading = false;
      this.stopped = false;
      this.loadedUrls = new Set();

      if (!this.grid || !('IntersectionObserver' in window)) {
        return;
      }

      this.fallback.hidden = true;

      if (!this.nextUrl) return;

      this.status.hidden = false;
      this.status.textContent = 'Scroll down for more products';

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: '0px 0px 300px 0px' }
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.initialized = false;
    }

    async loadNextPage() {
      if (
        this.loading ||
        this.stopped ||
        !this.nextUrl ||
        !this.isConnected
      ) {
        return;
      }

      this.loading = true;
      this.observer.unobserve(this);
      this.status.textContent = 'Loading more products…';
      this.grid.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();

      try {
        const url = new URL(this.nextUrl, window.location.href);

        if (
          url.origin !== window.location.origin ||
          this.loadedUrls.has(url.href)
        ) {
          throw new Error('Invalid pagination URL');
        }

        const response = await fetch(url.href, {
          signal: this.controller.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Could not load products');
        }

        const html = await response.text();

        if (!this.isConnected) return;

        const page = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const nextGrid = page.querySelector(
          '#ProductGridContainer ul#product-grid'
        );

        const nextScroller = page.querySelector(
          '#ProductGridContainer collection-infinite-scroll'
        );

        if (!nextGrid || !nextScroller) {
          throw new Error('Product grid not found');
        }

        const cards = Array.from(nextGrid.children);

        if (!cards.length) {
          throw new Error('No products returned');
        }

        cards.forEach((card) => {
          // Reveal appended cards without depending on Dawn's
          // initial page-load animation observer.
          [card, ...card.querySelectorAll('.scroll-trigger')]
            .forEach((element) => {
              element.classList.remove(
                'scroll-trigger',
                'scroll-trigger--offscreen',
                'animate--slide-in',
                'animate--fade-in'
              );
            });

          this.grid.appendChild(card);
        });

        this.loadedUrls.add(url.href);
        this.nextUrl = nextScroller.dataset.nextUrl || '';

        // Keep fallback links aligned with the latest loaded page.
        const nextFallback = nextScroller.querySelector(
          '[data-scroll-fallback]'
        );

        if (nextFallback) {
          this.fallback.innerHTML = nextFallback.innerHTML;
        }

        this.status.textContent =
          `${cards.length} more products loaded.`;

        if (!this.nextUrl) {
          this.stopped = true;
          this.status.textContent = 'All products loaded.';
          this.observer.disconnect();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.stopped = true;
          this.status.textContent =
            'Automatic loading paused. Use the page links below.';
          this.fallback.hidden = false;
          this.observer.disconnect();
        }
      } finally {
        this.loading = false;
        this.grid.removeAttribute('aria-busy');

        if (this.isConnected && this.nextUrl && !this.stopped) {
          this.observer.observe(this);
        }
      }
    }
  }

  customElements.define(
    'collection-infinite-scroll',
    CollectionInfiniteScroll
  );
}
